import type { WarEvent } from '../types/event.types'

export type WSStatus = 'connecting' | 'connected' | 'disconnecting' | 'disconnected' | 'error'

export interface WSClientOptions {
  sessionId: string
  teamId?: string
  token: string
  onEvent: (evt: WarEvent) => void
  onStatusChange: (status: WSStatus) => void
  onError?: (err: Event) => void
}

/**
 * War Room WebSocket client.
 * Connects to: ws://host/ws/war-room/sessions/:sessionId?team_id=:teamId
 *
 * Features:
 * - Auto-reconnect with exponential backoff (max 30s)
 * - Heartbeat (ping/pong)
 * - JSON event parsing with type narrowing
 */
export class WarRoomWSClient {
  private ws: WebSocket | null = null
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private reconnectAttempts = 0
  private maxReconnectAttempts = 10
  private baseReconnectDelay = 1000   // 1s
  private maxReconnectDelay = 30_000  // 30s
  private destroyed = false

  constructor(private readonly opts: WSClientOptions) {}

  connect(): void {
    if (this.destroyed) return

    const url = this.buildURL()
    this.opts.onStatusChange('connecting')

    this.ws = new WebSocket(url)

    this.ws.onopen = () => {
      this.reconnectAttempts = 0
      this.opts.onStatusChange('connected')
    }

    this.ws.onmessage = (evt: MessageEvent) => {
      try {
        const warEvent = JSON.parse(evt.data as string) as WarEvent
        this.opts.onEvent(warEvent)
      } catch {
        // Malformed JSON — ignore
      }
    }

    this.ws.onerror = (err) => {
      this.opts.onError?.(err)
    }

    this.ws.onclose = (evt) => {
      if (this.destroyed) {
        this.opts.onStatusChange('disconnected')
        return
      }
      // Unexpected close → reconnect
      if (!evt.wasClean) {
        this.scheduleReconnect()
      } else {
        this.opts.onStatusChange('disconnected')
      }
    }
  }

  disconnect(): void {
    this.destroyed = true
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    if (this.ws) {
      this.opts.onStatusChange('disconnecting')
      this.ws.close(1000, 'client disconnect')
      this.ws = null
    }
  }

  private buildURL(): string {
    const { sessionId, teamId, token } = this.opts
    const params = new URLSearchParams()
    if (teamId) params.set('team_id', teamId)
    // Token in query param (WS cannot set headers)
    params.set('token', token)
    const baseURL = window.location.origin.replace(/^http/, 'ws')
    return `${baseURL}/ws/war-room/sessions/${sessionId}?${params.toString()}`
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.opts.onStatusChange('error')
      return
    }

    const delay = Math.min(
      this.baseReconnectDelay * 2 ** this.reconnectAttempts,
      this.maxReconnectDelay
    )
    this.opts.onStatusChange('connecting')
    this.reconnectAttempts++

    this.reconnectTimer = setTimeout(() => {
      if (!this.destroyed) this.connect()
    }, delay)
  }
}
